/**
 * Track route history inside our app
 */

import { BUY_PRODUCT_CANCEL, BUY_PRODUCT_SUCCESS, INDEX, WebRoute } from 'config/fe-routes';
import Router, { useRouter } from 'next/router';
import { useEffect } from 'react';
import { setHistory } from 'store/actions/history.actions';
import { redirectTo } from 'utils/route-helpers';

let routeHistory = [];
let isPopped = false;
let lastUrl;

export const getPreviousHistoryTrackerRoute = (history) => routeHistory?.[history?.length - 2];

export const useBackNavigation = () => {
  const router = useRouter();

  const navigateBackOrRedirect = (fallbackRedirect: WebRoute) => {
    const currentPath = router.asPath;
    const previousPath = routeHistory.at(-2)?.as;

    if (previousPath && previousPath !== currentPath) {
      Router.back();
    } else {
      redirectTo(fallbackRedirect);
    }
  };

  return {
    navigateBackOrRedirect,
  };
};

export default function useRouteHistoryTracker(router) {
  useEffect(() => {
    const savedHistory = sessionStorage.getItem('history');
    const parsedHistory = JSON.parse(savedHistory);

    if (parsedHistory && parsedHistory?.length > 0) {
      routeHistory = parsedHistory;
      setHistory(routeHistory);
    }
  }, []);

  useEffect(() => {
    // Handle special cases that happen on Pop
    const handlePopSpecialCases = ({ route }) => {
      const isCheckoutResultPage = [
        BUY_PRODUCT_SUCCESS('', '').href,
        BUY_PRODUCT_CANCEL('', '').href,
        '/checkout/success',
        '/checkout/cancel',
      ].some((checkoutResultHref) => route?.startsWith(checkoutResultHref));

      // IF trying to pop to checkout success/cancel page, redirect to index
      if (isCheckoutResultPage) {
        redirectTo(INDEX());
      }
    };

    // When route change complete, restore scroll position
    const onRouteChangeComplete = (url) => {
      if (lastUrl === router.asPath) {
        return;
      }
      lastUrl = router.asPath;

      // If route changed, push to history array
      if (routeHistory[routeHistory.length - 1]?.as !== url) {
        // If url was replaced (check route-helpers redirectTo() for more info)
        if (router.query?.replace === 'true') {
          routeHistory[routeHistory.length ? routeHistory.length - 1 : 0] = {
            href: router.route,
            as: router.asPath,
            replace: true,
          };
        } else {
          // Push url
          routeHistory.push({ href: router.route, as: router.asPath });
        }
      }

      // If last path is index, might as well clear history because we don't have a back functionality for index page
      if (routeHistory[routeHistory.length - 1]?.as === '/') {
        routeHistory = [INDEX()];
        sessionStorage.removeItem('history');
      }

      // Check if back/forward was clicked
      if (isPopped) {
        handlePopSpecialCases(router);
      }

      // Save to store
      setHistory(routeHistory);
      sessionStorage.setItem('history', JSON.stringify(routeHistory));

      // Reset flag for next route to check if back/forward button was clicked
      isPopped = false;
    };

    // Before back/forward button click handler
    const beforePopState = () => {
      isPopped = true;
    };

    // On back/forward button click handler
    const onPopState = (event) => {
      const previousRoute = routeHistory[routeHistory?.length - 2]?.as;
      const newRoute = event?.state?.as;

      if (previousRoute === newRoute) {
        routeHistory.pop();
      }
    };

    // Initial run
    onRouteChangeComplete('/');

    // Init events
    Router.events.on('routeChangeComplete', onRouteChangeComplete);
    window.addEventListener('popstate', onPopState);
    window.addEventListener('beforepopstate', beforePopState);

    // Clear events
    return () => {
      Router.events.off('routeChangeComplete', onRouteChangeComplete);
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('beforepopstate', beforePopState);
    };
  }, [router]);
}
